<template>
  <div class="subscription-container">
    <div v-if="loading" class="loading">
      Loading subscriptions...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else class="dashboard">
      <!-- Metrics Overview -->
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-icon active">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div class="metric-info">
            <h3>Active Subscriptions</h3>
            <p class="metric-value">{{ metrics.total_active }}</p>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-icon cancelled">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <div class="metric-info">
            <h3>Cancelled Subscriptions</h3>
            <p class="metric-value">{{ metrics.total_cancelled }}</p>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-icon churn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 3v18h18"/>
              <path d="M18 9l-5 5-4-4-3 3"/>
            </svg>
          </div>
          <div class="metric-info">
            <h3>Churn Rate</h3>
            <p class="metric-value">{{ metrics.churn_rate }}%</p>
          </div>
        </div>
      </div>

      <!-- Platform Distribution -->
      <div class="analytics-row">
        <div class="analytics-card">
          <h3>Subscriptions by Platform</h3>
          <div class="platform-stats">
            <div v-for="platform in metrics.subscriptions_by_platform" :key="platform.platform" class="platform-item">
              <span class="platform-name">{{ platform.platform }}</span>
              <span class="platform-count">{{ platform.count }}</span>
            </div>
          </div>
        </div>

        <!-- Plan Distribution -->
        <div class="analytics-card">
          <h3>Subscriptions by Plan</h3>
          <div class="plan-stats">
            <div v-for="plan in metrics.subscriptions_by_plan" :key="plan.plan_id" class="plan-item">
              <span class="plan-name">{{ plan.plan.name }}</span>
              <span class="plan-count">{{ plan.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Monthly Trend -->
      <div class="analytics-card monthly-trend">
        <h3>Monthly Subscriptions</h3>
        <div class="monthly-stats">
          <div v-for="month in metrics.monthly_subscriptions" :key="month.month" class="month-item">
            <span class="month-label">{{ formatMonth(month.month) }}</span>
            <div class="month-bar" :style="{ height: month.total * 20 + 'px' }"></div>
            <span class="month-value">{{ month.total }}</span>
          </div>
        </div>
      </div>

      <!-- Recent Activities -->
      <div class="analytics-card">
        <h3>Recent Activities</h3>
        <div class="activities-list">
          <div v-for="activity in metrics.recent_activities" :key="activity.id" class="activity-item">
            <div class="activity-user">
              <img v-if="activity.user?.img_url" :src="activity.user.img_url" :alt="getUserFullName(activity.user)" class="user-avatar">
              <div class="activity-details">
                <strong>{{ getUserFullName(activity.user) }}</strong>
                <span class="activity-plan">{{ activity.plan.name }}</span>
              </div>
            </div>
            <div class="activity-info">
              <span :class="['status', activity.status]">{{ activity.status }}</span>
              <span class="activity-date">{{ formatDate(activity.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscription List -->
      <div class="subscription-list">
        <h2>All Subscriptions</h2>
        <div v-for="subscription in subscriptionData" :key="subscription.id" class="subscription-card">
          <div class="subscription-header">
            <div class="user-info">
              <img v-if="subscription.user?.img_url" :src="subscription.user.img_url" :alt="getUserFullName(subscription.user)" class="user-avatar">
              <div class="user-details">
                <h3>{{ getUserFullName(subscription.user) }}</h3>
                <span class="user-email">{{ subscription.user?.email }}</span>
              </div>
            </div>
            <span :class="['status', subscription.status]">{{ subscription.status }}</span>
          </div>
          <div class="subscription-details">
            <div class="detail-row">
              <p><strong>Plan:</strong> {{ subscription.plan?.name }}</p>
              <p><strong>Price:</strong> ${{ subscription.plan?.value }}</p>
            </div>
            <div class="detail-row">
              <p><strong>Platform:</strong> {{ subscription.platform }}</p>
              <p><strong>Start Date:</strong> {{ formatDate(subscription.start_date) }}</p>
            </div>
            <div class="detail-row">
              <p><strong>End Date:</strong> {{ formatDate(subscription.end_date) }}</p>
              <p v-if="subscription.cancelled_at"><strong>Cancelled:</strong> {{ formatDate(subscription.cancelled_at) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.last_page > 1" class="pagination">
        <button 
          :disabled="pagination.current_page === 1" 
          @click="changePage(pagination.current_page - 1)"
          class="pagination-btn"
        >
          Previous
        </button>
        <span class="page-info">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
        <button 
          :disabled="pagination.current_page === pagination.last_page" 
          @click="changePage(pagination.current_page + 1)"
          class="pagination-btn"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SubscriptionList',
  data() {
    return {
      subscriptionData: [],
      metrics: {
        total_active: 0,
        total_cancelled: 0,
        churn_rate: 0,
        subscriptions_by_plan: [],
        subscriptions_by_platform: [],
        monthly_subscriptions: [],
        recent_activities: []
      },
      loading: true,
      error: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0
      }
    };
  },
  methods: {
    async fetchSubscriptions(page = 1) {
      try {
        const [subscriptionsResponse, metricsResponse] = await Promise.all([
          axios.request({
            method: 'get',
            url: `https://api.kewaar.com/api/admin/subscriptions?page=${page}`,
            headers: { 
              'Authorization': 'Bearer 4846|Xz62Lte34IshohbttoDYNoQ8js1nKJYLYphsTIe2a84c2412'
            }
          }),
          axios.request({
            method: 'get',
            url: 'https://api.kewaar.com/api/admin/subscriptions/metrics',
            headers: { 
              'Authorization': 'Bearer 4846|Xz62Lte34IshohbttoDYNoQ8js1nKJYLYphsTIe2a84c2412'
            }
          })
        ]);

        if (subscriptionsResponse.data.success) {
          this.subscriptionData = subscriptionsResponse.data.data.data;
          this.pagination = {
            current_page: subscriptionsResponse.data.data.current_page,
            last_page: subscriptionsResponse.data.data.last_page,
            total: subscriptionsResponse.data.data.total
          };
        }

        if (metricsResponse.data.success) {
          this.metrics = metricsResponse.data.data;
        }

        this.loading = false;
      } catch (err) {
        this.error = 'Failed to fetch subscription data. Please try again later.';
        this.loading = false;
        console.error('Error:', err);
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },
    formatMonth(monthString) {
      if (!monthString) return 'N/A';
      const [year, month] = monthString.split('-');
      return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },
    getUserFullName(user) {
      if (!user) return 'Unknown User';
      return `${user.first_name} ${user.last_name}`;
    },
    async changePage(page) {
      this.loading = true;
      await this.fetchSubscriptions(page);
    }
  },
  created() {
    this.fetchSubscriptions();
  }
};
</script>

<style scoped>
.subscription-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.dashboard {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-icon svg {
  width: 24px;
  height: 24px;
  color: white;
}

.metric-icon.active {
  background: #28a745;
}

.metric-icon.cancelled {
  background: #dc3545;
}

.metric-icon.churn {
  background: #ffc107;
}

.metric-info h3 {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin: 4px 0 0;
}

.analytics-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.analytics-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.analytics-card h3 {
  margin: 0 0 16px;
  color: #2c3e50;
}

.platform-stats, .plan-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.platform-item, .plan-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.platform-name, .plan-name {
  color: #666;
  text-transform: capitalize;
}

.platform-count, .plan-count {
  font-weight: bold;
  color: #2c3e50;
}

.monthly-trend {
  overflow: hidden;
}

.monthly-stats {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  padding: 20px 0;
}

.month-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.month-bar {
  width: 30px;
  background: #64B5F6;
  border-radius: 4px;
  transition: height 0.3s ease;
}

.month-label {
  font-size: 0.8rem;
  color: #666;
}

.month-value {
  font-weight: bold;
  color: #2c3e50;
}

.activities-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  background: #f8f9fa;
}

.activity-user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.activity-details {
  display: flex;
  flex-direction: column;
}

.activity-plan {
  font-size: 0.9rem;
  color: #666;
}

.activity-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.activity-date {
  font-size: 0.8rem;
  color: #666;
}

.subscription-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading, .error {
  text-align: center;
  padding: 20px;
  font-size: 1.1em;
}

.error {
  color: #dc3545;
}

.subscription-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-label {
  font-size: 0.9em;
  color: #666;
}

.stat-value {
  display: block;
  font-size: 1.5em;
  font-weight: bold;
  color: #2c3e50;
}

.subscription-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.subscription-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.subscription-card:hover {
  transform: translateY(-2px);
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-details h3 {
  margin: 0;
  color: #333;
}

.user-email {
  font-size: 0.9em;
  color: #666;
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
  text-transform: capitalize;
}

.status.active {
  background-color: #28a745;
  color: white;
}

.status.expired {
  background-color: #dc3545;
  color: white;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
}

.detail-row p {
  margin: 0;
  color: #666;
}

.detail-row strong {
  color: #333;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background: #f8f9fa;
}

.page-info {
  color: #666;
}
</style> 